/**
 * Act 1 Store — 第一幕：亲友初筛
 *
 * 管理第一幕的轮次、亲友态度值、手牌、消息等状态。
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Character } from '@shared/types/character';
import type { FamilyMember } from '@shared/types/family';
import type { Act1Card } from '@shared/types/cards';
import type { ChatMessage, SenderRole } from '@shared/types/gameState';
import { FAMILY_ATTITUDE_INITIAL, ACT1_HAND_SIZE } from '@shared/constants/gameBalance';
import { drawAct1Cards } from '@shared/constants/act1Cards';

type Act1Phase = 'WAITING' | 'PLAYER_TURN' | 'PROCESSING' | 'FINISHED';

interface Act1StoreState {
  round: number;
  maxRounds: number;
  phase: Act1Phase;
  messages: ChatMessage[];

  // 亲友态度
  familyAttitudes: Record<string, number>;

  // 卡牌系统
  deck: Act1Card[];
  hand: Act1Card[];

  // 好感度（第一幕也会小幅影响）
  affectionA: number;
  affectionB: number;

  // 瞬态 UI
  isTyping: boolean;
  typingSpeaker: string | null;

  // 流式对话状态
  currentSpeaker: string | null;
  streamingText: string;
  streamingAgentName: string | null;
  streamingAgentRole: SenderRole | null;

  // 卡牌叙事流式状态
  cardNarrativeText: string;
  isCardNarrating: boolean;

  // 上下文摘要缓存
  historySummary: string | null;
  lastSummarizedIndex: number;
}

interface Act1StoreActions {
  initialize: (
    protagonistA: Character,
    protagonistB: Character,
    familyA: FamilyMember[],
    familyB: FamilyMember[],
  ) => void;

  // 轮次
  nextRound: () => void;
  setPhase: (phase: Act1Phase) => void;

  // 消息
  appendMessage: (msg: ChatMessage) => void;
  appendMessages: (msgs: ChatMessage[]) => void;

  // 态度
  updateAttitude: (memberId: string, delta: number) => void;
  setAttitudes: (attitudes: Record<string, number>) => void;

  // 好感
  adjustAffection: (side: 'A' | 'B', delta: number) => void;

  // 卡牌
  playCard: (cardId: string) => Act1Card | undefined;
  refillHand: () => void;

  // UI
  setIsTyping: (v: boolean) => void;
  setTypingSpeaker: (id: string | null) => void;

  // 流式对话
  setCurrentSpeaker: (id: string | null) => void;
  appendStreamingText: (text: string) => void;
  clearStreaming: () => void;
  /** 将当前流式文本转为正式 ChatMessage 并追加到 messages */
  commitStreamingMessage: () => void;
  setStreamingAgent: (name: string | null, role: SenderRole | null) => void;

  // 卡牌叙事
  setIsCardNarrating: (v: boolean) => void;
  appendCardNarrative: (text: string) => void;
  commitCardNarrative: (cardName: string, fullText: string) => void;
  clearCardNarrative: () => void;

  // 摘要
  setHistorySummary: (summary: string, lastIndex: number) => void;

  // 重置
  reset: () => void;
}

export type Act1Store = Act1StoreState & Act1StoreActions;

const INITIAL_STATE: Act1StoreState = {
  round: 1,
  maxRounds: 8,
  phase: 'WAITING',
  messages: [],
  familyAttitudes: {},
  deck: [],
  hand: [],
  affectionA: 50,
  affectionB: 50,
  isTyping: false,
  typingSpeaker: null,
  currentSpeaker: null,
  streamingText: '',
  streamingAgentName: null,
  streamingAgentRole: null,
  cardNarrativeText: '',
  isCardNarrating: false,
  historySummary: null,
  lastSummarizedIndex: 0,
};

export const useAct1Store = create<Act1Store>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      initialize: (_protagonistA, _protagonistB, familyA, familyB) => {
        // 初始化亲友态度
        const attitudes: Record<string, number> = {};
        [...familyA, ...familyB].forEach(m => {
          attitudes[m.id] = FAMILY_ATTITUDE_INITIAL;
        });

        // 从30张池中抽取15张组成本局牌库
        const fullDeck = drawAct1Cards(15);
        const hand = fullDeck.slice(0, ACT1_HAND_SIZE);
        const deck = fullDeck.slice(ACT1_HAND_SIZE);

        set({
          ...INITIAL_STATE,
          familyAttitudes: attitudes,
          deck,
          hand,
          phase: 'WAITING',
        });
      },

      nextRound: () => set(s => ({ round: s.round + 1 })),
      setPhase: (phase) => set({ phase }),

      appendMessage: (msg) => set(s => ({ messages: [...s.messages, msg] })),
      appendMessages: (msgs) => set(s => ({ messages: [...s.messages, ...msgs] })),

      updateAttitude: (memberId, delta) => set(s => ({
        familyAttitudes: {
          ...s.familyAttitudes,
          [memberId]: Math.max(-50, Math.min(50, (s.familyAttitudes[memberId] ?? 0) + delta)),
        },
      })),

      setAttitudes: (attitudes) => set({ familyAttitudes: attitudes }),

      adjustAffection: (side, delta) => set(s => {
        if (side === 'A') return { affectionA: Math.max(0, Math.min(100, s.affectionA + delta)) };
        return { affectionB: Math.max(0, Math.min(100, s.affectionB + delta)) };
      }),

      playCard: (cardId) => {
        const state = get();
        const card = state.hand.find(c => c.id === cardId);
        if (!card) return undefined;
        set({ hand: state.hand.filter(c => c.id !== cardId) });
        return card;
      },

      refillHand: () => set(s => {
        const needed = ACT1_HAND_SIZE - s.hand.length;
        if (needed <= 0 || s.deck.length === 0) return s;
        const drawn = s.deck.slice(0, needed);
        return {
          hand: [...s.hand, ...drawn],
          deck: s.deck.slice(needed),
        };
      }),

      setIsTyping: (v) => set({ isTyping: v }),
      setTypingSpeaker: (id) => set({ typingSpeaker: id }),

      // 流式对话
      setCurrentSpeaker: (id) => set({ currentSpeaker: id }),
      appendStreamingText: (text) => set(s => ({ streamingText: s.streamingText + text })),
      clearStreaming: () => set({ streamingText: '', streamingAgentName: null, streamingAgentRole: null }),
      setStreamingAgent: (name, role) => set({ streamingAgentName: name, streamingAgentRole: role }),
      commitStreamingMessage: () => set(s => {
        if (!s.streamingText || !s.currentSpeaker) return s;
        const msg: ChatMessage = {
          id: `act1_stream_${Date.now()}_${s.currentSpeaker}`,
          senderId: s.currentSpeaker,
          senderName: s.streamingAgentName || '未知',
          senderRole: s.streamingAgentRole || 'system',
          text: s.streamingText,
          type: 'text',
        };
        return {
          messages: [...s.messages, msg],
          streamingText: '',
          streamingAgentName: null,
          streamingAgentRole: null,
        };
      }),

      // 卡牌叙事
      setIsCardNarrating: (v) => set({ isCardNarrating: v }),
      appendCardNarrative: (text) => set(s => ({ cardNarrativeText: s.cardNarrativeText + text })),
      commitCardNarrative: (cardName, fullText) => set(s => {
        const msg: ChatMessage = {
          id: `act1_narrative_${Date.now()}`,
          senderId: 'system',
          senderName: cardName,
          senderRole: 'system',
          text: fullText,
          type: 'narrative',
        };
        return {
          messages: [...s.messages, msg],
          cardNarrativeText: '',
          isCardNarrating: false,
        };
      }),
      clearCardNarrative: () => set({ cardNarrativeText: '', isCardNarrating: false }),

      // 摘要
      setHistorySummary: (summary, lastIndex) => set({ historySummary: summary, lastSummarizedIndex: lastIndex }),

      reset: () => set({ ...INITIAL_STATE }),
    }),
    {
      name: 'cm-v2-act1',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        round: state.round,
        maxRounds: state.maxRounds,
        phase: state.phase,
        messages: state.messages,
        familyAttitudes: state.familyAttitudes,
        deck: state.deck,
        hand: state.hand,
        affectionA: state.affectionA,
        affectionB: state.affectionB,
        historySummary: state.historySummary,
        lastSummarizedIndex: state.lastSummarizedIndex,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.isTyping = false;
        state.typingSpeaker = null;
        state.currentSpeaker = null;
        state.streamingText = '';
        state.streamingAgentName = null;
        state.streamingAgentRole = null;
        state.cardNarrativeText = '';
        state.isCardNarrating = false;
      },
    },
  ),
);
